/**
 * SoleadLab 2.0 - Project Import (ZIP)
 *
 * Reads a ZIP backup file, uploads assets to R2,
 * and restores the project state.
 */

import JSZip from 'jszip';
import type { ProjectData, ProjectMeta } from '../stores/projectStore';
import type { ExportManifest } from './projectExport';
import { uploadAsset } from './assetApi';

export interface ImportResult {
  data: ProjectData;
  meta: Partial<ProjectMeta>;
  manifest?: ExportManifest;
}

/**
 * Import a project from a ZIP file.
 *
 * If siteId is provided, assets are uploaded to R2.
 * If not, relative asset paths are converted to blob URLs for local preview.
 */
export async function importProjectZip(
  zipFile: File,
  siteId?: string,
  onProgress?: (message: string) => void
): Promise<ImportResult> {
  onProgress?.('Reading ZIP file...');
  const zipData = await zipFile.arrayBuffer();
  const zip = await JSZip.loadAsync(zipData);

  // Read manifest
  let manifest: ExportManifest | undefined;
  const manifestFile = zip.file('manifest.json');
  if (manifestFile) {
    try {
      const raw = await manifestFile.async('text');
      manifest = JSON.parse(raw);
    } catch {
      // Proceed without manifest
    }
  }

  // Read project data
  const projectFile = zip.file('project.json');
  if (!projectFile) {
    throw new Error('Invalid ZIP: missing project.json');
  }

  const projectRaw = await projectFile.async('text');
  const projectData = JSON.parse(projectRaw) as ProjectData;

  // ── Restore overlay assets ──
  onProgress?.('Restoring overlays...');
  for (let i = 0; i < projectData.overlays.length; i++) {
    const overlay = projectData.overlays[i];
    if (!overlay.url || !overlay.url.startsWith('assets/')) continue;

    const assetFile = zip.file(overlay.url);
    if (!assetFile) continue;

    const blob = await assetFile.async('blob');

    if (siteId) {
      try {
        const result = await uploadAsset(blob, siteId, 'overlay', `${overlay.id}.png`);
        projectData.overlays[i] = { ...overlay, url: result.url };
        continue;
      } catch {
        // Fall back to blob URL
      }
    }

    projectData.overlays[i] = { ...overlay, url: URL.createObjectURL(blob) };
  }

  // ── Restore orthophoto assets ──
  onProgress?.('Restoring orthophotos...');
  for (let i = 0; i < projectData.orthophotos.length; i++) {
    const ortho = projectData.orthophotos[i];
    if (!ortho.url || !ortho.url.startsWith('assets/')) continue;

    const assetFile = zip.file(ortho.url);
    if (!assetFile) continue;

    const blob = await assetFile.async('blob');

    if (siteId) {
      try {
        const result = await uploadAsset(blob, siteId, 'orthophoto', `${ortho.id}.png`);
        projectData.orthophotos[i] = { ...ortho, url: result.url };
        continue;
      } catch {
        // Fall back to blob URL
      }
    }

    projectData.orthophotos[i] = { ...ortho, url: URL.createObjectURL(blob) };
  }

  // ── Restore photo assets ──
  onProgress?.('Restoring photos...');
  for (let i = 0; i < projectData.indications.length; i++) {
    const ind = projectData.indications[i];
    if (ind.type !== 'Photo' || !ind.properties.image) continue;
    if (!ind.properties.image.startsWith('assets/')) continue;

    const assetFile = zip.file(ind.properties.image);
    if (!assetFile) continue;

    const blob = await assetFile.async('blob');
    const ext = ind.properties.image.split('.').pop() || 'jpg';

    if (siteId) {
      try {
        const result = await uploadAsset(blob, siteId, 'photo', `${ind.id}.${ext}`);
        projectData.indications[i] = {
          ...ind,
          properties: { ...ind.properties, image: result.url },
        };
        continue;
      } catch {
        // Fall back to blob URL
      }
    }

    projectData.indications[i] = {
      ...ind,
      properties: { ...ind.properties, image: URL.createObjectURL(blob) },
    };
  }

  // ── Restore geo photo assets ──
  onProgress?.('Restoring geo photos...');
  for (let i = 0; i < projectData.geoPhotos.length; i++) {
    const gp = projectData.geoPhotos[i];
    if (!gp.url || !gp.url.startsWith('assets/')) continue;

    const assetFile = zip.file(gp.url);
    if (!assetFile) continue;

    const blob = await assetFile.async('blob');

    if (siteId) {
      try {
        const result = await uploadAsset(blob, siteId, 'geo_photo', `${gp.id}.jpg`);
        projectData.geoPhotos[i] = { ...gp, url: result.url };
        continue;
      } catch {
        // Fall back to blob URL
      }
    }

    projectData.geoPhotos[i] = { ...gp, url: URL.createObjectURL(blob) };
  }

  onProgress?.('Import complete');

  return {
    data: projectData,
    meta: {
      siteName: manifest?.siteName || zipFile.name.replace(/\.zip$/i, ''),
      siteId,
    },
    manifest,
  };
}
